// Filterleiste des Verlaufs (Runde 7B, L55, L58 bis L60): Suchfeld, Nutzer, Kontakt, Zeitraum. Gleiche Bausteine für den Log-Reiter.
// Die Typ-Reiter (Alle, Anrufe, …) stehen darüber im Feed. Alles ist ein Filter am Server (`GET /api/leads/{id}/activities`).
import { useEffect, useRef, useState } from "react";
import type { Kontakt } from "../../api";
import { useMeta } from "../../daten/meta";
import { datumText, heute, tagPlus } from "../../werkzeuge/zeit";
import { Ausklapp } from "../Ausklapp";
import { Auswahl } from "../Auswahl";
import { Icon } from "../Icon";
import { TL } from "./leadTexte";
import "./leadTiefe.css";
import { DatumFeld } from "../DatumFeld";

export type ZeitWahl = "heute" | "woche" | "monat" | "quartal" | "eigen";

export interface ZeitFilterWert {
  zeit?: ZeitWahl | null;
  von?: string | null;
  bis?: string | null;
}

/** von und bis (Berliner Tage) für eine Voreinstellung */
export function zeitraumFuer(w: ZeitWahl): { von: string; bis: string | null } {
  const h = heute();
  if (w === "heute") return { von: h, bis: h };
  if (w === "woche") return { von: tagPlus(h, -6), bis: null };
  if (w === "monat") return { von: tagPlus(h, -29), bis: null };
  if (w === "quartal") return { von: tagPlus(h, -89), bis: null };
  return { von: h, bis: null };
}

export function SucheFeld({
  wert,
  onChange,
  platzhalter = TL.suche.platzhalter,
  eingabeRef,
  zahl,
}: {
  wert: string;
  onChange: (q: string) => void;
  platzhalter?: string;
  eingabeRef?: React.Ref<HTMLInputElement>;
  /** Trefferzahl hinter dem Feld (nur bei Suchtext) */
  zahl?: number | null;
}) {
  const [text, setText] = useState(wert);
  const letzter = useRef(wert);
  // von außen zurückgesetzt (Filter zurücksetzen, anderer Lead)
  useEffect(() => {
    if (wert !== letzter.current) {
      letzter.current = wert;
      setText(wert);
    }
  }, [wert]);
  // Tippen wartet kurz, damit nicht jeder Buchstabe eine Abfrage auslöst
  useEffect(() => {
    if (text.trim() === letzter.current.trim()) return;
    const t = window.setTimeout(() => {
      letzter.current = text.trim();
      onChange(text.trim());
    }, 280);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <label className="lf-suche" title={TL.suche.tipp}>
      <Icon name="suche" groesse={13} />
      <input
        ref={eingabeRef}
        type="search"
        className="lf-suche__eingabe"
        placeholder={platzhalter}
        aria-label="Im Verlauf suchen"
        value={text}
        spellCheck={false}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && text) {
            e.preventDefault();
            e.stopPropagation();
            setText("");
            letzter.current = "";
            onChange("");
          }
          if (e.key === "Enter") {
            letzter.current = text.trim();
            onChange(text.trim());
          }
        }}
      />
      {zahl !== undefined && zahl !== null && text.trim() ? <span className="lf-suche__zahl">{TL.suche.treffer(zahl)}</span> : null}
      {text ? (
        <button
          type="button"
          className="lf-suche__weg"
          title={TL.suche.leeren}
          aria-label={TL.suche.leeren}
          onClick={() => {
            setText("");
            letzter.current = "";
            onChange("");
          }}
        >
          <Icon name="schliessen" groesse={11} />
        </button>
      ) : null}
    </label>
  );
}

export function NutzerFilter({ wert, onChange }: { wert: string | null | undefined; onChange: (id: string | null) => void }) {
  const { nutzerListe } = useMeta();
  return (
    <Auswahl
      optionen={nutzerListe.filter((u) => u.is_active || u.id === wert).map((u) => ({ wert: u.id, label: u.name }))}
      wert={wert ?? null}
      leerErlaubt
      platzhalter={TL.filter.alleNutzer}
      knopfKlasse={`lf-knopf${wert ? " lf-knopf--an" : ""}`}
      breite={240}
      titel="Nach Nutzer filtern"
      onWahl={(w) => onChange((w as string | null) ?? null)}
    />
  );
}

export function KontaktFilter({ kontakte, wert, onChange }: { kontakte: Kontakt[]; wert: string | null | undefined; onChange: (id: string | null) => void }) {
  return (
    <Auswahl
      optionen={kontakte.map((k) => ({ wert: k.id, label: k.name || k.display_name || "Kontakt" }))}
      wert={wert ?? null}
      leerErlaubt
      platzhalter={TL.filter.alleKontakte}
      knopfKlasse={`lf-knopf${wert ? " lf-knopf--an" : ""}`}
      breite={240}
      titel="Nach Kontakt filtern"
      onWahl={(w) => onChange((w as string | null) ?? null)}
    />
  );
}

const ZEIT_NAMEN: Record<ZeitWahl, string> = {
  heute: TL.filter.zeitraum.heute,
  woche: TL.filter.zeitraum.woche,
  monat: TL.filter.zeitraum.monat,
  quartal: TL.filter.zeitraum.quartal,
  eigen: TL.filter.zeitraum.eigen,
};

export function ZeitFilter({ wert, onChange }: { wert: ZeitFilterWert; onChange: (w: ZeitFilterWert) => void }) {
  const [offen, setOffen] = useState(false);
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [von, setVon] = useState(wert.von ?? "");
  const [bis, setBis] = useState(wert.bis ?? "");
  const aktiv = !!(wert.zeit || wert.von || wert.bis);
  useEffect(() => {
    if (offen) {
      setVon(wert.von ?? "");
      setBis(wert.bis ?? "");
    }
  }, [offen, wert.von, wert.bis]);
  let text: string = TL.filter.alleZeit;
  if (wert.zeit && wert.zeit !== "eigen") text = ZEIT_NAMEN[wert.zeit];
  else if (wert.von || wert.bis) text = `${wert.von ? datumText(wert.von) : "…"} – ${wert.bis ? datumText(wert.bis) : "heute"}`;
  const wahl = (w: ZeitWahl | null) => {
    if (!w) onChange({ zeit: null, von: null, bis: null });
    else onChange({ zeit: w, ...zeitraumFuer(w) });
    setOffen(false);
  };
  const eigenAnwenden = () => {
    if (!von && !bis) return wahl(null);
    onChange({ zeit: "eigen", von: von || null, bis: bis || null });
    setOffen(false);
  };
  return (
    <>
      <button
        ref={setAnker}
        type="button"
        className={`lf-knopf${aktiv ? " lf-knopf--an" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={offen}
        title="Nach Zeitraum filtern"
        onClick={() => setOffen((o) => !o)}
      >
        <Icon name="kalender" groesse={12} />
        <span className="lf-knopf__text">{text}</span>
        <Icon name="chevron-unten" groesse={11} />
      </button>
      <Ausklapp anker={anker} offen={offen} onSchliessen={() => setOffen(false)} breite={240} rolle="dialog">
        <div className="lf-zeit">
          <ul className="lf-zeit__liste">
            <li>
              <button type="button" className={`lf-zeit__punkt${!aktiv ? " lf-zeit__punkt--an" : ""}`} onClick={() => wahl(null)}>
                {TL.filter.alleZeit}
              </button>
            </li>
            {(["heute", "woche", "monat", "quartal"] as const).map((w) => (
              <li key={w}>
                <button type="button" className={`lf-zeit__punkt${wert.zeit === w ? " lf-zeit__punkt--an" : ""}`} onClick={() => wahl(w)}>
                  {ZEIT_NAMEN[w]}
                </button>
              </li>
            ))}
          </ul>
          <div className="lf-zeit__eigen">
            <label>
              <span>{TL.filter.von}</span>
              <DatumFeld className="eingabe eingabe--klein" value={von} max={bis || undefined} onChange={(e) => setVon(e.target.value)} />
            </label>
            <label>
              <span>{TL.filter.bis}</span>
              <DatumFeld className="eingabe eingabe--klein" value={bis} min={von || undefined} onChange={(e) => setBis(e.target.value)} />
            </label>
            <button type="button" className="knopf knopf--klein knopf--primaer" onClick={eigenAnwenden}>
              {TL.filter.anwenden}
            </button>
          </div>
        </div>
      </Ausklapp>
    </>
  );
}
